<template>
  <div>
    <Head title="Einstellungen" />
    <h1 class="mb-8 text-xl font-bold">Einstellungen</h1>
    <form @submit.prevent="update">
      <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden mb-8">
        <div class="p-8">
          <h2 class="text-lg font-medium mb-4">Wochentage</h2>
          <p class="text-sm text-gray-600 mb-4">
            Die Reihenfolge hier bestimmt auch die Sortierung in Listen und Berichten.
          </p>

          <div v-if="form.errors.weekdays" class="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 text-sm font-medium rounded">
            {{ form.errors.weekdays }}
          </div>

          <div v-for="(day, index) in form.weekdays" :key="index" class="flex flex-wrap items-end -mr-2 mb-4">
            <text-input v-model="day.value" :error="form.errors[`weekdays.${index}.value`]" class="pr-2 w-full lg:w-1/4" label="Kürzel (ID)" />
            <text-input v-model="day.label" :error="form.errors[`weekdays.${index}.label`]" class="pr-2 w-full lg:w-1/2" label="Name" />
            <div class="flex items-center space-x-2 pb-2">
              <button type="button" class="text-red-600 hover:text-red-800 font-medium text-sm px-2" title="Entfernen" @click="removeDay(index)">Entfernen</button>
              <button v-if="index > 0" type="button" class="bg-gray-200 hover:bg-gray-300 rounded p-1" title="Nach oben" @click="moveDay(index, -1)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
              </button>
              <button v-if="index < form.weekdays.length - 1" type="button" class="bg-gray-200 hover:bg-gray-300 rounded p-1" title="Nach unten" @click="moveDay(index, 1)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
            </div>
          </div>
          <button type="button" class="mt-2 text-indigo-600 hover:text-indigo-800 font-bold text-sm" @click="addDay">
            + Tag hinzufügen
          </button>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Wochentage speichern</loading-button>
        </div>
      </div>

      <div v-if="account.has_module_supporter_requests" class="max-w-3xl bg-white rounded-md shadow overflow-hidden mb-8">
        <div class="p-8">
          <div class="mb-6">
            <h2 class="text-lg font-medium mb-4">Helfer-Anmeldung</h2>
            <select-input v-model="form.supporter_registration_mode" :error="form.errors.supporter_registration_mode" class="pb-8 pr-6 w-full lg:w-1/2" label="Anmelde-Modus">
              <option value="general">Allgemeine Anmeldung (Tage, Kategorie)</option>
              <option value="detailed">Detaillierte Anmeldung (Tage, Ressort Name)</option>
              <option value="very_detailed">Sehr detailliert (Konkrete Schicht)</option>
            </select-input>
            <p class="text-sm text-gray-600 -mt-4 mb-8">
              Bestimmt, wie detailliert sich Helfer über das öffentliche Formular anmelden können.
            </p>

            <p class="text-sm text-gray-600 -mt-4 mb-4">
              Verfügbare Platzhalter: <strong>[Name]</strong>, <strong>[Ressort]</strong>.
            </p>
            <textarea-input v-model="form.supporter_counter_proposal_text" :error="form.errors.supporter_counter_proposal_text" class="pb-8 w-full" label="Gegenvorschlag E-Mail Text" rows="12" />
          </div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Einstellungen speichern</loading-button>
        </div>
      </div>

      <div v-if="account.has_module_supporter_requests" class="max-w-3xl bg-white rounded-md shadow overflow-hidden mb-8">
        <div class="p-8">
          <h2 class="text-lg font-medium mb-4">Helfer Widget Sicherheit</h2>
          <div class="mb-6">
            <text-input :model-value="account.api_key" label="Public API Key" readonly class="pb-4 pr-6 w-full" />
            <p class="text-sm text-gray-600 mb-4">
              Diesen Key im Widget-Snippet verwenden.
            </p>
            <button type="button" class="text-sm text-indigo-600 hover:text-indigo-800 font-bold mb-8" @click="regenerateApiKey">
              API Key neu generieren
            </button>

            <textarea-input v-model="form.allowed_origins" :error="form.errors.allowed_origins" class="pb-4 w-full" label="Erlaubte Domains (CORS)" rows="3" />
            <p class="text-sm text-gray-600">
              Eine Domain pro Zeile. Leer lassen, um alle Domains zu erlauben (nicht empfohlen).
            </p>
          </div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Sicherheitseinstellungen speichern</loading-button>
        </div>
      </div>

      <div v-if="account.has_module_weather" class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
        <div class="p-8">
          <div class="mb-6">
            <h2 class="text-lg font-medium mb-4">Agrarwetter</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
              <text-input v-model="form.weather_lat" :error="form.errors.weather_lat" class="pb-8 w-full" label="Breitengrad (lat)" placeholder="52.2295" />
              <text-input v-model="form.weather_lon" :error="form.errors.weather_lon" class="pb-8 w-full" label="Längengrad (lon)" placeholder="8.9090" />
              <text-input v-model="form.weather_location" :error="form.errors.weather_location" class="pb-8 w-full" label="Ortsname" placeholder="Porta Westfalica" />
              <text-input v-model="form.weather_dwd_region" :error="form.errors.weather_dwd_region" class="pb-8 w-full" label="DWD Regions-ID" placeholder="805774004" />
              <text-input v-model="form.weather_festival_start" :error="form.errors.weather_festival_start" class="pb-8 w-full" label="Festival-Start (JJJJ-MM-TT)" placeholder="2026-07-01" />
              <text-input v-model="form.weather_festival_end" :error="form.errors.weather_festival_end" class="pb-8 w-full" label="Festival-Ende (JJJJ-MM-TT)" placeholder="2026-07-28" />
            </div>
            <p class="text-sm text-gray-600 -mt-4">
              Wetterdaten werden stündlich während des Festivals gespeichert (max. 4 Wochen).
            </p>
          </div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Einstellungen speichern</loading-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    SelectInput,
    TextInput,
    TextareaInput,
    LoadingButton,
  },
  layout: Layout,
  props: {
    account: Object,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        supporter_registration_mode: this.account.supporter_registration_mode,
        weather_lat: this.account.weather_lat,
        weather_lon: this.account.weather_lon,
        weather_location: this.account.weather_location,
        weather_dwd_region: this.account.weather_dwd_region,
        weather_festival_start: this.account.weather_festival_start,
        weather_festival_end: this.account.weather_festival_end,
        supporter_counter_proposal_text: this.account.supporter_counter_proposal_text,
        allowed_origins: this.account.allowed_origins,
        weekdays: this.account.weekdays,
      }),
    }
  },
  methods: {
    update() {
      this.form.put('/settings')
    },
    regenerateApiKey() {
      if (confirm('Bist du sicher? Der alte API Key wird sofort ungültig und das Widget funktioniert auf externen Seiten erst nach Aktualisierung des Keys wieder.')) {
        this.$inertia.post('/settings/regenerate-api-key')
      }
    },
    addDay() {
      this.form.weekdays.push({ value: '', label: '' })
    },
    removeDay(index) {
      this.form.weekdays.splice(index, 1)
    },
    moveDay(index, direction) {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < this.form.weekdays.length) {
        const temp = this.form.weekdays[index]
        this.form.weekdays[index] = this.form.weekdays[newIndex]
        this.form.weekdays[newIndex] = temp
      }
    },
  },
}
</script>
